import { LitElement, html, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import styles from '../../styles/mariana-scroll-dots.css?inline';

interface DotConfig {
  index: number;
  isActive: boolean;
  size: 'full' | 'medium' | 'small';
}

/**
 * Scroll Dots Component for Card Carousels
 * Displays dynamic dots indicator with up to 7 dots (last 2 decrease in size)
 * Can be used with recipes, coupons, and other card-based components
 */
@customElement('mariana-scroll-dots')
export class MarianaScrollDots extends LitElement {
  static readonly styles = unsafeCSS(styles);

  @property({ type: Number })
  totalCards: number = 0;

  @property({ type: Number })
  currentIndex: number = 0;

  /**
   * Determine the size category for a dot based on its distance from the active dot
   * Dots farthest from current position become smaller (symmetrical behavior)
   */
  private getDotSize(index: number): 'full' | 'medium' | 'small' {
    const distance = Math.abs(index - this.currentIndex);
    const maxDistance = Math.max(this.currentIndex, this.totalCards - 1 - this.currentIndex);
    
    if (distance === maxDistance) return 'small';
    return distance === maxDistance - 1 ? 'medium' : 'full';
  }

  /**
   * Calculates which dots to display
   * Shows exactly as many dots as there are cards (1 card = 0 dots)
   */
  private getDots(): DotConfig[] {
    // If only 1 card, show no dots
    if (this.totalCards <= 1) {
      return [];
    }

    // Show exactly as many dots as cards
    return Array.from({ length: this.totalCards }, (_, i) => ({
      index: i,
      isActive: i === this.currentIndex,
      size: this.getDotSize(i),
    }));
  }

  private handleDotClick(index: number) {
    this.dispatchEvent(
      new CustomEvent('dot-click', {
        detail: { index },
        bubbles: true,
        composed: true,
      })
    );
  }

  render() {
    if (this.totalCards <= 1) {
      return html`<div class="scroll-dots-container"></div>`;
    }

    return html`
      <div class="scroll-dots-container">
        ${this.getDots().map((dot) => html`
          <button
            class="scroll-dot ${dot.size} ${dot.isActive ? 'active' : ''}"
            @click=${() => this.handleDotClick(dot.index)}
            type="button"
            aria-label="Go to card ${dot.index + 1}"
          ></button>
        `)}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mariana-scroll-dots': MarianaScrollDots;
  }
}
